/**
 * Candidate Requests — the client half of the inbound-question endpoints.
 *
 * Split out of `taActionBoardApi.ts` when that file hit the 500-line ceiling; every existing caller
 * reaches these through `import * as api from './taActionBoardApi'`, which re-exports the module, so
 * the split is invisible to them.
 *
 * Two things every call here has in common: the recruiter's own short-lived Gmail access token
 * travels in a HEADER (never a query param, which would land in logs and history), and a lapsed
 * token is handled as the routine event it is (`GmailSessionExpired`) rather than surfacing as a
 * global error.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type {
  InboundMessage,
  InboundResponse,
} from '../types/taActionBoard';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board`;

/**
 * Inbound candidate questions from the signed-in user's own Gmail inbox.
 *
 * Takes no email argument on purpose: the proxy derives the mailbox from the Firebase token, so
 * there is nothing to pass and no way to ask for someone else's inbox.
 */
export class GmailSessionExpired extends Error {
  constructor() {
    super('Gmail session expired');
    this.name = 'GmailSessionExpired';
  }
}

/**
 * The proxy refused a SECOND reply to the same candidate message (409).
 *
 * Not a failure to retry: it means the answer is already on its way or already sent. Distinct from
 * ApiRequestError so the caller can retire the card instead of offering "try again".
 */
export class DuplicateReply extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DuplicateReply';
  }
}

/** The caller's own Gmail access token travels in a header, never a query param. */
const gmailHeader = (token: string) => ({ 'X-Gmail-Token': token });

/** `search` is handed to Gmail itself, so it reaches mail outside the default two-week queue. */

export async function fetchRequests(
  gmailToken: string,
  search?: string
): Promise<InboundResponse> {
  const qs = search ? `?q=${encodeURIComponent(search)}` : '';
  const res = await authenticatedFetch(`${BASE}/requests${qs}`, {
    headers: gmailHeader(gmailToken),
    // authenticatedFetch throws AND reports a global error on any non-OK status. A lapsed
    // Gmail token is routine, not an incident, so 401 is handled here instead of surfacing.
    suppressErrorStatuses: [401],
  });
  if (res.status === 401) throw new GmailSessionExpired();
  return res.json() as Promise<InboundResponse>;
}

/**
 * SEND the reviewed reply on the candidate's own thread.
 *
 * Called only after the 10-second countdown in `usePendingActions` expires, so "undo" means the
 * request never happens. The proxy reads the recipient off the Gmail thread, so this cannot be
 * pointed at another address.
 */
export async function sendRequestReply(payload: {
  threadId: string;
  /** The caller's own short-lived Gmail access token. */
  gmailToken: string;
  /** HTML from the editor; the proxy sanitises it and sends it as HTML. */
  body: string;
  /**
   * The candidate's Lever opportunity. The proxy resolves which POSTING they are on from it — the
   * pilot's single-posting test gate needs that, and fails closed without it. Null for a sender not
   * in Lever. It is an id to look up, never a posting to trust.
   */
  opportunityId?: string | null;
  /**
   * No subject and no messageId: the proxy reads both off the Gmail thread. A client-named subject
   * could retitle the candidate's conversation, and a client-named message could aim the reply at
   * one of our own messages or slip past the server's duplicate guard.
   */
}): Promise<{
  ok: boolean;
  messageId: string | null;
  to: string;
  subject: string;
  sent: true;
  /**
   * What became of the thread in the mailbox after the reply went out: labelled `Spaces`, read and
   * archived ('updated'), or not — a stale token needs a reconnect. Never affects `ok`: the reply has
   * already been delivered by the time any of this runs.
   */
  triaged?: 'updated' | 'failed' | 'skipped' | 'needs_reconnect';
  /**
   * Whether our OWN `answeredAt` record landed — a different question from `triaged`, which is about
   * Gmail. False means the reply went out but nothing recorded it, so the thread will come back as
   * unanswered work and invite a second reply to the same question. The one post-send failure worth
   * telling the recruiter about. Absent on a proxy that predates the field, which must read as fine.
   */
  recorded?: boolean;
}> {
  const { threadId, gmailToken, ...rest } = payload;
  const res = await authenticatedFetch(`${BASE}/requests/${encodeURIComponent(threadId)}/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...gmailHeader(gmailToken) },
    body: JSON.stringify(rest),
    suppressErrorStatuses: [401, 409],
  });
  // Any other failure has already thrown ApiRequestError carrying the proxy's own message.
  if (res.status === 401) throw new GmailSessionExpired();
  if (res.status === 409) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new DuplicateReply(detail?.error || 'This message has already been answered.');
  }
  return res.json() as Promise<{
    ok: boolean;
    messageId: string | null;
    to: string;
    subject: string;
    sent: true;
  }>;
}

/**
 * The proxy refused to serve the bytes because the attachment is too large (413). Carries the
 * Gmail deep link so the chip can offer the fallback instead of a dead error.
 */
export class AttachmentTooLarge extends Error {
  gmailUrl: string | null;
  constructor(message: string, gmailUrl: string | null) {
    super(message);
    this.name = 'AttachmentTooLarge';
    this.gmailUrl = gmailUrl;
  }
}

/**
 * ONE attachment's bytes, fetched when the recruiter clicks its chip.
 *
 * Returns a Blob whose type the proxy already restricted to an inline-safe allowlist (raster
 * images + PDF; everything else arrives as application/octet-stream and downloads). The bytes
 * live in memory only — object URLs, never localStorage — same rule as requestsCache.
 */
export async function fetchAttachment(
  ref: { threadId: string; messageId: string; attachmentId: string },
  gmailToken: string
): Promise<Blob> {
  const url =
    `${BASE}/requests/${encodeURIComponent(ref.threadId)}` +
    `/messages/${encodeURIComponent(ref.messageId)}` +
    `/attachments/${encodeURIComponent(ref.attachmentId)}`;
  const res = await authenticatedFetch(url, {
    headers: gmailHeader(gmailToken),
    suppressErrorStatuses: [401, 413],
  });
  if (res.status === 401) throw new GmailSessionExpired();
  if (res.status === 413) {
    const detail = (await res.json().catch(() => null)) as {
      error?: string;
      gmailUrl?: string;
    } | null;
    throw new AttachmentTooLarge(
      detail?.error || 'Too large to fetch here — open it in Gmail.',
      detail?.gmailUrl || null
    );
  }
  return res.blob();
}

/**
 * The WHOLE conversation on one thread, fetched when the recruiter opens the history.
 *
 * The queue embeds only the last few messages per thread, so this is what makes "see the whole
 * thread" true rather than approximately true. One thread, asked for deliberately — which is why
 * it is not folded into the queue payload.
 */
export async function fetchRequestThread(
  threadId: string,
  gmailToken: string
): Promise<{ conversation: InboundMessage[]; total: number }> {
  const res = await authenticatedFetch(
    `${BASE}/requests/${encodeURIComponent(threadId)}/thread`,
    { headers: gmailHeader(gmailToken), suppressErrorStatuses: [401] }
  );
  if (res.status === 401) throw new GmailSessionExpired();
  return res.json() as Promise<{ conversation: InboundMessage[]; total: number }>;
}



/**
 * Mark a request handled: our own triage state, AND — since Aug 2026 — the mailbox itself, where the
 * thread is marked read, labelled `Spaces` and (unless `archive: false`) archived.
 *
 * The Gmail token is optional on purpose. Triage is what makes the thread stop coming back, so the
 * proxy records it either way and reports what happened to the mailbox separately:
 *
 *   'updated'         both done
 *   'failed'          triage recorded, mailbox untouched (logged server-side, not the user's problem)
 *   'skipped'         no token attached, or demo mode
 *   'needs_reconnect' the recruiter's token predates the gmail.modify scope — show the connect
 *                     banner, but the request IS handled and must leave the queue
 */
/**
 * FINN cancels the candidate's interview. Lives in interviewsApi (the booking domain owns it);
 * re-exported here because the Candidate Requests card is its main caller.
 */
export {
  cancelInterviewBooking,
  fetchBookingSlots,
  previewRescheduleEmail,
  rescheduleInterviewBooking,
  STAFF_RESCHEDULE_REASONS,
} from './interviewsApi';

/**
 * Rate a drafted reply. `down` requires a comment — a thumbs-down without a word is a mood, not a
 * signal, and the whole point is knowing WHICH drafts to fix.
 *
 * Failure is swallowed by the caller: this is telemetry, and a lost vote must not read as a broken
 * button in the middle of someone's reply.
 */
export async function rateDraft(payload: {
  threadId: string;
  messageId?: string;
  vote: 'up' | 'down';
  comment?: string;
  category?: string | null;
  draftSource?: string | null;
  /** Which board surface the widget sat on — more of them are coming. */
  tab?: string;
  /**
   * The candidate's own message and the text we drafted, so a vote can be read back with the thing
   * it was a verdict ON. Candidate content: it goes to our own store, behind the usage permission,
   * and never to an analytics sink.
   */
  subject?: string;
  candidateText?: string;
  draftText?: string | null;
}): Promise<{ ok: boolean }> {
  const { threadId, ...rest } = payload;
  const res = await authenticatedFetch(
    `${BASE}/requests/${encodeURIComponent(threadId)}/draft-feedback`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rest),
    }
  );
  return res.json() as Promise<{ ok: boolean }>;
}

/**
 * @param archive what happens to the MAIL, never to our own record — the request is marked handled
 *   and marked read either way. `true` (the default, and what `✓ Archive in Gmail` sends) also takes
 *   the thread out of the inbox; `false` (`✕ Remove from overview`) leaves it there.
 */
export async function dismissRequest(
  threadId: string,
  gmailToken?: string | null,
  { archive = true }: { archive?: boolean } = {}
): Promise<DismissResult> {
  const res = await authenticatedFetch(`${BASE}/requests/${encodeURIComponent(threadId)}/dismiss`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(gmailToken ? gmailHeader(gmailToken) : {}),
    },
    body: JSON.stringify({ archive }),
  });
  return res.json() as Promise<DismissResult>;
}

export interface DismissResult {
  ok: boolean;
  gmail?: 'updated' | 'failed' | 'skipped' | 'needs_reconnect';
  /** What the proxy actually did to the mailbox, so the toast states it rather than assuming it. */
  archived?: boolean;
}

/**
 * "Help me write": rewrite the open draft according to one typed instruction.
 *
 * **The only function in this file that takes no Gmail token**, and the reason is worth stating: the
 * rewrite touches nothing in the mailbox. It sends the text the recruiter is looking at, gets text
 * back, and the reply only ever leaves through `sendRequestReply` — which reads the recipient and
 * subject off the Gmail thread and sits behind the 10-second countdown.
 *
 * A refusal (`ok: false`) is a normal outcome, not an error: the model was asked for something the
 * policy guard will not put in front of a candidate, or it did not come back. The caller must leave
 * the editor exactly as it was in that case — a refused rewrite must never cost the recruiter their
 * own words.
 */
export async function refineRequestReply(payload: {
  threadId: string;
  /** The draft as it currently stands in the editor, as plain text. */
  body: string;
  /** What the recruiter typed. Capped server-side; over the cap answers 400. */
  instruction: string;
  /** The candidate's own message, for context — never re-answered from scratch. */
  question: string;
  category: string;
  language: 'de' | 'en';
  candidateFirstName: string;
  /**
   * What the card shows above the editor, so "add more feedback" has a source: the interviewers'
   * internal note and the decision log's AGG paragraph. Optional; capped server-side.
   */
  context?: { feedbackNote?: string; decisionFeedback?: string };
}): Promise<RefineResult> {
  const { threadId, ...rest } = payload;
  const res = await authenticatedFetch(`${BASE}/requests/${encodeURIComponent(threadId)}/refine`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rest),
  });
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(detail?.error || 'The rewrite could not be run.');
  }
  return res.json() as Promise<RefineResult>;
}

export interface RefineResult {
  ok: boolean;
  /** The rewritten body as plain text. Present only when `ok`. */
  text?: string;
  /**
   * The same reply as editor HTML: escaped, newlines as <br>, and `[phrase](url)` turned into a real
   * anchor for allow-listed URLs. Prefer it over `text` when present (older proxies omit it).
   */
  html?: string;
  /**
   * Specifics the rewrite states that nothing in front of the recruiter supports. Advisory by
   * product decision (Sofia, Sep 1 2026) — recruiters know things Lever does not, so an unsupported
   * fact is flagged for checking rather than refused.
   */
  warnings?: string[];
  /** Why nothing was applied. Present only when `ok` is false; the editor must be left alone. */
  refusal?: string;
}
